import React, { useCallback, useState } from "react";
import { TextField, MenuItem, FormControlLabel, Checkbox, Radio, RadioGroup, Autocomplete } from "@mui/material";
import PropTypes from "prop-types";

const validationPatterns = {
  alphabet: /^[A-Za-z\s]*$/,
  alphanumeric: /^[A-Za-z0-9\s]*$/,
  numeric: /^[0-9]*$/,
  float: /^[0-9]*\.?[0-9]*$/,
};

const validateEmail = (email, required) => {
  if (!email && required) return "Email is required";
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return email && !regex.test(email) ? "Invalid email format" : "";
};

export const CommonFormFields = ({ type = "text", id, label, value, onChange, options = [], required = false, multiple = false, validationType = null, rows = 1, ...props }) => {
  const [error, setError] = useState("");

  // Phone number formatting and validation
  const formatPhoneNumber = raw => {
    raw = raw.replace(/\D/g, "").slice(0, 10);
    if (raw.length === 0) return "";
    if (/^04/.test(raw)) {
      if (raw.length <= 4) return raw;
      if (raw.length <= 7) return `${raw.slice(0, 4)} ${raw.slice(4)}`;
      return `${raw.slice(0, 4)} ${raw.slice(4, 7)} ${raw.slice(7, 10)}`;
    } else if (/^0[2378]/.test(raw)) {
      if (raw.length <= 2) return raw;
      if (raw.length <= 6) return `${raw.slice(0, 2)} ${raw.slice(2)}`;
      return `${raw.slice(0, 2)} ${raw.slice(2, 6)} ${raw.slice(6, 10)}`;
    }
    if (raw.length <= 4) return raw;
    if (raw.length <= 7) return `${raw.slice(0, 4)} ${raw.slice(4)}`;
    return `${raw.slice(0, 4)} ${raw.slice(4, 7)} ${raw.slice(7, 10)}`;
  };

  const validatePhone = raw => {
    const digits = raw.replace(/\D/g, "");
    return digits.length === 10;
  };

  // ✅ Unified input change handler
  const handleInputChange = useCallback(
    (e, newValue) => {
      try {
        let inputValue;

        // Case 1: Autocomplete (has newValue)
        if (type === "autocomplete") {
          inputValue = newValue ?? "";
        }
        // Case 2: Normal input/select/checkbox/radio/date
        else if (e && e.target) {
          if (type === "checkbox") {
            inputValue = e.target.checked;
          } else {
            inputValue = e.target.value ?? "";
          }
        } else {
          console.warn("Unexpected onChange payload:", e);
          return;
        }

        let isValid = true;
        setError("");

        // Basic validation by type
        if (type === "text" && validationType) {
          if (inputValue && !validationPatterns[validationType]?.test(inputValue)) {
            return;
          }
        } else if (type === "email") {
          const validationError = validateEmail(inputValue, required);
          setError(validationError);
          isValid = !validationError;
        } else if (type === "phone") {
          const rawValue = inputValue.replace(/\D/g, "").slice(0, 10);
          inputValue = formatPhoneNumber(rawValue);
          const phoneValid = validatePhone(rawValue);
          setError(rawValue && !phoneValid ? "Phone number must be 10 digits" : "");
          isValid = phoneValid;
        }

        // Fire the parent handler safely
        onChange && onChange({ id, value: inputValue, isValid });
      } catch (err) {
        console.error("Error in CommonFormFields.handleInputChange:", err);
      }
    },
    [id, type, validationType, required, onChange]
  );

  // Handle phone keydown
  const handlePhoneKeyDown = e => {
    if (type === "phone") {
      const invalidKeys = ["e", "E", "+", "-", ".", ","];
      if (invalidKeys.includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  // ------------------- Render -------------------
  switch (type) {
    case "select":
      return (
        <TextField id={id} label={label} value={value ?? ""} onChange={handleInputChange} variant="standard" required={required} fullWidth select SelectProps={{ multiple }} error={!!error} helperText={error} {...props}>
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      );

    case "textarea":
      return <TextField id={id} label={label} value={value ?? ""} onChange={handleInputChange} variant="standard" required={required} fullWidth multiline rows={rows} error={!!error} helperText={error} {...props} />;

    case "checkbox":
      return <FormControlLabel control={<Checkbox id={id} checked={!!value} onChange={handleInputChange} required={required} {...props} />} label={label} />;

    case "radio":
      return (
        <RadioGroup id={id} value={value ?? ""} onChange={handleInputChange}>
          {options.map(option => (
            <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />
          ))}
        </RadioGroup>
      );

    case "autocomplete":
      return <Autocomplete id={id} options={options} getOptionLabel={option => option.label || ""} value={options.find(opt => opt.value === value) || null} onChange={(e, newValue) => handleInputChange(e, newValue ? newValue.value : "")} renderInput={params => <TextField {...params} label={label} variant="standard" required={required} error={!!error} helperText={error} />} {...props} />;

    default:
      return <TextField id={id} label={label} value={value ?? ""} onChange={handleInputChange} onKeyDown={type === "phone" ? handlePhoneKeyDown : undefined} variant="standard" required={required} fullWidth type={type === "phone" ? "tel" : type === "text" && validationType === "numeric" ? "tel" : type === "date" ? "date" : type} inputProps={type === "phone" ? { inputMode: "numeric", maxLength: 12 } : undefined} error={!!error} helperText={error} {...props} />;
  }
};

CommonFormFields.propTypes = {
  type: PropTypes.oneOf(["text", "textarea", "email", "phone", "select", "checkbox", "radio", "autocomplete", "date"]),
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.arrayOf(PropTypes.string)]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  required: PropTypes.bool,
  multiple: PropTypes.bool,
  validationType: PropTypes.oneOf(["alphabet", "alphanumeric", "numeric", null]),
  rows: PropTypes.number,
};
